import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { FileText, Target, Zap, Check, ArrowRight, Star, Shield, TrendingUp } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import MainMenu from '@/components/MainMenu';
import SeoHead from '@/components/SeoHead';
import SiteDateReadout from '@/components/SiteDateReadout';

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "AI Resume Analysis",
      description: "Get instant feedback with a detailed score out of 100"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Job Match Comparison",
      description: "Compare your resume against any job description"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "ATS Review",
      description: "Review formatting and keyword alignment for applicant tracking systems"
    }
  ];

  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "",
      features: [
        "2 resume analyses per month",
        "1 resume vs job comparison per month",
        "1 custom tailored resume generation per month",
        "Resume score out of 100",
        "Improvement feedback"
      ],
      cta: "Get Started",
      popular: false
    },
    {
      name: "Pro",
      price: "$4.99",
      period: "/month",
      features: [
        "Unlimited resume analyses",
        "Unlimited resume vs job comparisons",
        "1 custom tailored resume generation per month",
        "Keyword matching analysis",
        "Missing skills identification",
        "ATS compatibility score"
      ],
      cta: "Upgrade to Pro",
      popular: true
    },
    {
      name: "Premium",
      price: "$9.99",
      period: "/month",
      features: [
        "Everything in Pro",
        "AI resume builder",
        "Resume bullet rewriting",
        "Custom job-tailored cover letter generation",
        "Unlimited custom tailored resumes",
        "Export as PDF/DOCX",
        "Powered by Claude AI"
      ],
      cta: "Go Premium",
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="JOBFITAI | AI Resume Analysis and Tailoring"
        description="Analyze your resume, compare it to job descriptions, and tailor stronger applications with JOBFITAI."
        canonicalPath="/"
        robots="index,follow"
      />
      {/* Navigation */}
      <nav className="border-b border-border bg-white/80 backdrop-blur-lg fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MainMenu />
            <AppLogo data-testid="logo" />
          </div>
          <div className="hidden sm:flex items-center gap-4">
            <button
              onClick={() => navigate('/pricing')}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-pricing"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/resources')}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-resources"
            >
              Resources
            </button>
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover transition-all"
                data-testid="nav-dashboard"
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="nav-login"
                >
                  Log in
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover transition-all"
                  data-testid="nav-register"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Star className="w-4 h-4" />
              AI-Powered Resume Analysis
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-6" data-testid="hero-headline">
              Instantly Compare Your Resume<br />to a Job Description
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Review your resume, identify missing keywords, and tailor it for a specific role with AI-powered analysis.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/register')}
                className="bg-primary text-white px-8 py-4 rounded-lg font-medium hover:bg-primary-hover transition-all shadow-medium flex items-center justify-center gap-2 btn-active"
                data-testid="hero-cta"
              >
                Check My Resume Score
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-white text-foreground px-8 py-4 rounded-lg font-medium border border-border hover:bg-surface transition-all"
                data-testid="hero-pricing-btn"
              >
                View Pricing
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-surface">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              Everything You Need to Land Your Dream Job
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our AI analyzes your resume against real job requirements to help you stand out.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-border rounded-xl p-6 shadow-soft hover:shadow-medium transition-all"
                data-testid={`feature-${index}`}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              How It Works
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Upload Your Resume", desc: "Paste your resume text or upload a PDF file" },
              { step: "2", title: "Get Your Score", desc: "Receive an instant analysis with a score out of 100" },
              { step: "3", title: "Improve & Apply", desc: "Use our suggestions to optimize and land interviews" }
            ].map((item, index) => (
              <div key={index} className="text-center" data-testid={`step-${index}`}>
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center font-heading text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6 bg-surface" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that fits your job search needs.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative bg-white border rounded-xl p-6 ${
                  plan.popular 
                    ? 'border-primary shadow-medium ring-2 ring-primary/20' 
                    : 'border-border shadow-soft'
                }`}
                data-testid={`plan-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-white text-xs font-medium px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-heading text-xl font-semibold mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-heading text-4xl font-bold">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
                  className={`w-full py-3 rounded-lg font-medium transition-all ${
                    plan.popular
                      ? 'bg-primary text-white hover:bg-primary-hover'
                      : 'bg-surface text-foreground hover:bg-surface-highlight border border-border'
                  }`}
                  data-testid={`plan-${plan.name.toLowerCase()}-cta`}
                >
                  {plan.cta}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { icon: <Shield className="w-8 h-8" />, value: "Secure", label: "& Private" },
              { icon: <TrendingUp className="w-8 h-8" />, value: "Better Resume", label: "Alignment" },
              { icon: <Star className="w-8 h-8" />, value: "AI-powered", label: "Resume Analysis" }
            ].map((stat, index) => (
              <div key={index} data-testid={`stat-${index}`}>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mx-auto mb-4">
                  {stat.icon}
                </div>
                <div className="font-heading text-3xl font-bold mb-1">{stat.value}</div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-secondary text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
            Ready to Optimize Your Resume?
          </h2>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Build a clearer, job-focused resume before you apply.
          </p>
          <button
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
            className="bg-white text-secondary px-8 py-4 rounded-lg font-medium hover:bg-white/90 transition-all"
            data-testid="final-cta"
          >
            Get Started Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <AppLogo variant="footer" />
          <div className="flex flex-col items-center md:items-end gap-2">
            <div className="flex flex-wrap items-center justify-center md:justify-end gap-6">
              <button
                onClick={() => navigate('/privacy')}
                className="text-muted-foreground text-sm hover:text-foreground transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => navigate('/terms')}
                className="text-muted-foreground text-sm hover:text-foreground transition-colors"
              >
                Terms of Service
              </button>
              <p className="text-muted-foreground text-sm">
                © 2026 JobFit AI. All rights reserved.
              </p>
            </div>
            <SiteDateReadout />
          </div>
        </div>
      </footer>
    </div>
  );
}
